/**
 * Math Settings Component
 * Number ranges for each operation + timer
 */

import { useState } from 'react'
import { useSettingsStore } from '@stores/settingsStore'
import { OPERATION_LIMITS } from '@config/constants'

interface MathSettingsProps {
  isOpen: boolean
  onToggle: (open: boolean) => void
}

export default function MathSettings({ isOpen, onToggle }: MathSettingsProps) {
  const settings = useSettingsStore(state => state.settings)
  const updateMaxValue = useSettingsStore(state => state.updateMaxValue)
  const updateEnabledOperations = useSettingsStore(state => state.updateEnabledOperations)
  const updateMode = useSettingsStore(state => state.updateMode)
  const toggleMode = () => updateMode(settings.mode === 'multipleChoice' ? 'free' : 'multipleChoice')
  const [expandedOp, setExpandedOp] = useState<string | null>(null)

  const operations = [
    { id: 'multiplication', label: 'Multiplication', symbol: '×', limits: OPERATION_LIMITS.MULTIPLICATION },
    { id: 'division', label: 'Division', symbol: '÷', limits: OPERATION_LIMITS.DIVISION },
    { id: 'addition', label: 'Addition', symbol: '+', limits: OPERATION_LIMITS.ADDITION },
    { id: 'subtraction', label: 'Subtraction', symbol: '−', limits: OPERATION_LIMITS.SUBTRACTION },
  ] as const

  return (
    <details
      className="config__section"
      open={isOpen}
      onToggle={e => onToggle(e.currentTarget.open)}
    >
      <summary className="config__section-summary">
        <span className="config__section-title">Math Settings</span>
      </summary>
      <fieldset className="config__group config__group--framed">
        <legend>Math practice options</legend>
        <p className="config__hint config__hint--spaced">
          Choose which operations to practice, set their ranges, and pick how answers are entered.
        </p>
        <div className="config__operations-grid">
          {operations.map(op => {
            const isEnabled = settings.enabledOperations[op.id]
            const isExpanded = expandedOp === op.id

            return (
              <div key={op.id} className="config__operation-tile">
                {/* Collapsed view with integrated toggle */}
                {!isExpanded ? (
                  <div className={`config__operation-collapsed ${!isEnabled ? 'config__operation-collapsed--disabled' : ''}`}>
                    <label className="config__operation-toggle">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={e => {
                          updateEnabledOperations({ [op.id]: e.target.checked })
                          if (!e.target.checked) {
                            setExpandedOp(null)
                          }
                        }}
                        className="config__operation-toggle-input"
                      />
                      <span className="config__operation-toggle-slider"></span>
                    </label>
                    <button
                      type="button"
                      className="config__operation-collapsed-content"
                      onClick={() => {
                        if (isEnabled) {
                          setExpandedOp(op.id)
                        }
                      }}
                      disabled={!isEnabled}
                    >
                      <span className="config__operation-collapsed-label">{op.label}</span>
                      <span className="config__operation-collapsed-value">
                        {isEnabled ? `Max ${settings.maxValues[op.id]}` : 'Off'}
                      </span>
                    </button>
                  </div>
                ) : null}

                {/* Expanded view */}
                {isExpanded ? (
                  <div className="config__operation-expanded">
                    <div className="config__operation-header">
                      <span className="config__operation-label">{op.label}</span>
                    </div>
                    <div className="config__operation-body">
                      <div className="config__operation-input-row">
                        <div className="config__operation-copy">
                          <label htmlFor={`max${op.id}Input`} className="config__operation-subtitle">
                            Max value
                          </label>
                          <p className="config__operation-hint">Highest number we'll use for this set.</p>
                        </div>
                        <div className="config__number-picker">
                          <button
                            type="button"
                            className="config__number-picker-btn config__number-picker-btn--minus"
                            onClick={() => {
                              const newValue = Math.max(op.limits.min, settings.maxValues[op.id] - 1)
                              updateMaxValue(op.id, newValue)
                            }}
                            aria-label={`Decrease ${op.label} max value`}
                          >
                            −
                          </button>
                          <div className="config__number-picker-value">
                            <input
                              type="number"
                              id={`max${op.id}Input`}
                              min={op.limits.min}
                              max={op.limits.max}
                              step="1"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={settings.maxValues[op.id]}
                              onChange={e => {
                                const val = parseInt(e.target.value)
                                if (!isNaN(val)) {
                                  updateMaxValue(op.id, Math.max(op.limits.min, Math.min(op.limits.max, val)))
                                }
                              }}
                              className="config__number-picker-number"
                              aria-label={`${op.label} max value`}
                            />
                          </div>
                          <button
                            type="button"
                            className="config__number-picker-btn config__number-picker-btn--plus"
                            onClick={() => {
                              const newValue = Math.min(op.limits.max, settings.maxValues[op.id] + 1)
                              updateMaxValue(op.id, newValue)
                            }}
                            aria-label={`Increase ${op.label} max value`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="config__operation-collapse-btn"
                        onClick={() => setExpandedOp(null)}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
        <div className="config__section-divider" role="presentation" />
        <div
          className={`config__operation-collapsed ${settings.mode !== 'multipleChoice' ? 'config__operation-collapsed--disabled' : ''}`}
        >
          <label className="config__operation-toggle">
            <input
              type="checkbox"
              checked={settings.mode === 'multipleChoice'}
              onChange={toggleMode}
              className="config__operation-toggle-input"
            />
            <span className="config__operation-toggle-slider"></span>
          </label>
          <div className="config__operation-collapsed-content config__operation-content--static">
            <span className="config__operation-collapsed-label">Multiple Choice</span>
            <span className="config__operation-collapsed-value">
              {settings.mode === 'multipleChoice' ? 'On' : 'Off'}
            </span>
          </div>
        </div>
      </fieldset>
    </details>
  )
}
